import React, { useState, useEffect, useCallback } from 'react';
import { 
  CssBaseline, 
  Box, 
  Typography, 
  Tabs, 
  TabList, 
  Tab, 
  TabPanel, 
  Button, 
  Modal, 
  ModalDialog, 
  ModalClose, 
  Table, 
  Sheet, 
  Input, 
  Divider,
  Alert,
  IconButton,
  FormControl,
  FormLabel,
  Stack,
  Card,
  Chip,
  LinearProgress,
  AspectRatio,
  CircularProgress
} from '@mui/joy';
import { useNavigate } from 'react-router-dom';
import EvaluationForm, { serializeEvaluation, deserializeEvaluation, createEmptyEvaluation } from './EvaluationForm';
import IcdManager from './IcdManager';
import VorhaltepauschaleManager from './VorhaltepauschaleManager';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

// Helper to get base API URL - empty string means relative to current host
const getApiBaseUrl = () => {
  return '';  // Use relative paths for all API requests
};

// Helper functions to handle evaluation names with extensions
const addEvalExtension = (name) => {
  if (!name.endsWith('.eval')) {
    return `${name}.eval`;
  }
  return name;
};

const removeEvalExtension = (name) => {
  if (name.endsWith('.eval')) {
    return name.substring(0, name.length - 5);
  }
  return name;
};

function Admin() {
  const [evaluations, setEvaluations] = useState([]);
  const [services, setServices] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const navigate = useNavigate();
  const [adminUser, setAdminUser] = useState(null);
  
  // Form view states
  const [showAdvancedView, setShowAdvancedView] = useState(false);
  const [showAddEvalModal, setShowAddEvalModal] = useState(false);
  const [showEditEvalModal, setShowEditEvalModal] = useState(false);
  const [showAddServiceModal, setShowAddServiceModal] = useState(false);
  const [showEditServiceModal, setShowEditServiceModal] = useState(false);
  
  // Current data being edited
  const [currentEvaluation, setCurrentEvaluation] = useState(createEmptyEvaluation());
  const [currentService, setCurrentService] = useState({ code: '', description: '' });
  
  // License management state
  const [practices, setPractices] = useState([]);
  const [showAddPracticeModal, setShowAddPracticeModal] = useState(false);
  const [showEditPracticeModal, setShowEditPracticeModal] = useState(false);
  const [currentPractice, setCurrentPractice] = useState({ name: '', bsnr: '' });
  const [loadingLicense, setLoadingLicense] = useState(false);
  const [currentPracticeIndex, setCurrentPracticeIndex] = useState(-1);
  
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });

  // Add a progress state for bulk import
  const [importProgress, setImportProgress] = useState(0);

  // Add states for appearance management
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [favicon, setFavicon] = useState(null);
  const [faviconPreview, setFaviconPreview] = useState('');
  const [appearanceUpdating, setAppearanceUpdating] = useState(false);

  // Add state for application name
  const [appName, setAppName] = useState('QuEry-Tool');
  const [appNameInput, setAppNameInput] = useState('');

  // Add state for migration
  const [showMigrationModal, setShowMigrationModal] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);
  const [isMigrating, setIsMigrating] = useState(false);

  // Add state for login logs
  const [loginLogs, setLoginLogs] = useState([]);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  const [logsDateFilter, setLogsDateFilter] = useState('all'); // 'today', 'week', 'month', 'all'
  const [logsStatusFilter, setLogsStatusFilter] = useState('all'); // 'success', 'failed', 'all'
  const [logsBsnrFilter, setLogsBsnrFilter] = useState('all'); // 'all' or specific BSNR
  const [availablePractices, setAvailablePractices] = useState([]); // List of practices for filtering
  const [logsPagination, setLogsPagination] = useState({
    page: 0,
    pageSize: 50,
    totalCount: 0
  });

  // Load evaluations and services on component mount
  const loadEvaluations = useCallback(async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/evaluations`);
      if (!response.ok) {
        throw new Error('Failed to load evaluations list.');
      }
      const data = await response.json();
      
      // Add position information if not present
      const evaluationsWithPosition = data.map((item, index) => ({
        ...item,
        position: item.position !== undefined ? item.position : index
      }));
      
      // Sort by position
      evaluationsWithPosition.sort((a, b) => a.position - b.position);
      setEvaluations(evaluationsWithPosition);
    } catch (error) {
      console.error('Error loading evaluations:', error);
      showNotification('Fehler beim Laden der Auswertungen', 'error');
    }
  }, []);

  const loadServices = useCallback(async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/services`);
      if (!response.ok) {
        throw new Error('Failed to load services list.');
      }
      const data = await response.json();
      setServices(data);
    } catch (error) {
      console.error('Error loading services:', error);
      showNotification('Fehler beim Laden der Gebührenordnungspositionen', 'error');
    }
  }, []);

  const loadPractices = useCallback(async () => {
    try {
      setLoadingLicense(true);
      const response = await fetch(`${getApiBaseUrl()}/api/license`);
      if (!response.ok) {
        if (response.status === 404) {
          // License file doesn't exist yet, this is fine
          setPractices([]);
          return;
        }
        throw new Error('Failed to load license data.');
      }
      const data = await response.json();
      setPractices(data.practices || []);
    } catch (error) {
      console.error('Error loading license data:', error);
      showNotification('Fehler beim Laden der Lizenzinformationen', 'error');
    } finally {
      setLoadingLicense(false);
    }
  }, []);

  const loadAppearanceSettings = useCallback(async () => {
    try {
      // Load current logo preview
      setLogoPreview(`${getApiBaseUrl()}/logo.svg?t=${Date.now()}`);
      
      // Load current favicon preview
      setFaviconPreview(`${getApiBaseUrl()}/favicon.ico?t=${Date.now()}`);
      
      // Load application name setting
      const response = await fetch(`${getApiBaseUrl()}/api/appearance/settings`);
      if (response.ok) {
        const settings = await response.json();
        if (settings.appName) {
          setAppName(settings.appName);
          setAppNameInput(settings.appName);
        }
      }
    } catch (error) {
      console.error('Error loading appearance settings:', error);
    }
  }, []);

  // Load login logs
  const loadLoginLogs = useCallback(async () => {
    try {
      setIsLoadingLogs(true);
      
      // Build query parameters
      const params = new URLSearchParams({
        page: logsPagination.page,
        pageSize: logsPagination.pageSize,
        dateFilter: logsDateFilter,
        statusFilter: logsStatusFilter,
        bsnrFilter: logsBsnrFilter
      });
      
      const response = await fetch(`${getApiBaseUrl()}/api/logs/logins?${params}`);
      
      if (!response.ok) {
        throw new Error('Failed to load login logs');
      }
      
      const data = await response.json();
      
      setLoginLogs(data.logs);
      if (data.practices) {
        setAvailablePractices(data.practices);
      }
      setLogsPagination(prev => ({
        ...prev,
        totalCount: data.totalCount || data.logs.length
      }));
    } catch (error) {
      console.error('Error loading login logs:', error);
      showNotification('Fehler beim Laden der Login-Logs', 'error');
    } finally {
      setIsLoadingLogs(false);
    }
  }, [logsPagination.page, logsPagination.pageSize, logsDateFilter, logsStatusFilter, logsBsnrFilter]);

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: 'success' });
    }, 3000);
  };

  useEffect(() => {
    loadEvaluations();
    loadServices();
    loadPractices();
    loadAppearanceSettings();
  }, [loadEvaluations, loadServices, loadPractices, loadAppearanceSettings]);
  
  // Load logs when tab changes to logs tab
  useEffect(() => {
    if (activeTab === 5) { // Adjust the index if needed based on where you add the tab
      loadLoginLogs();
    }
  }, [activeTab, loadLoginLogs]);

  // Load admin user information
  useEffect(() => {
    const checkAdminAuth = async () => {
      try {
        const response = await fetch('/api/admin/auth-status');
        const data = await response.json();
        
        if (!data.isAuthenticated) {
          navigate('/admin/login');
          return;
        }
        
        setAdminUser(data.user);
      } catch (error) {
        console.error('Error checking admin authentication:', error);
        showNotification('Fehler bei der Authentifizierungsprüfung', 'error');
      }
    };

    checkAdminAuth();
  }, [navigate]);

  // Logout handler
  const handleLogout = async () => {
    try {
      const response = await fetch('/api/admin/logout', {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        navigate('/admin/login');
      } else {
        showNotification('Fehler beim Abmelden', 'error');
      }
    } catch (error) {
      console.error('Logout error:', error);
      showNotification('Fehler beim Abmelden', 'error');
    }
  };

  // Password change state and handlers
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordChangeLoading, setPasswordChangeLoading] = useState(false);
  
  const handleChangePassword = async () => {
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      showNotification('Bitte füllen Sie alle Felder aus', 'error');
      return;
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showNotification('Die neuen Passwörter stimmen nicht überein', 'error');
      return;
    }
    
    if (passwordData.newPassword.length < 8) {
      showNotification('Das neue Passwort muss mindestens 8 Zeichen lang sein', 'error');
      return;
    }
    
    setPasswordChangeLoading(true);
    
    try {
      const response = await fetch('/api/admin/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        }),
        credentials: 'include'
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Fehler beim Ändern des Passworts');
      }
      
      showNotification('Passwort erfolgreich geändert');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setShowChangePasswordModal(false);
    } catch (error) {
      console.error('Error changing password:', error);
      showNotification(error.message || 'Fehler beim Ändern des Passworts', 'error');
    } finally {
      setPasswordChangeLoading(false);
    }
  };

  // Add a new evaluation to the server
  const addEvaluation = async (name, content) => {
    try {
      // Make sure the name has .eval extension
      const fullName = addEvalExtension(name);
      console.log('Adding evaluation:', { name: fullName, content });
      const response = await fetch(`${getApiBaseUrl()}/api/evaluations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name: fullName, content }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Server error when adding evaluation:', errorText);
        throw new Error(`Failed to add evaluation: ${errorText}`);
      }
      // Refresh the evaluations list
      await loadEvaluations();
      showNotification(`Auswertung "${name}" hinzugefügt`);
      return true;
    } catch (error) {
      console.error('Error adding evaluation:', error);
      showNotification(`Fehler beim Hinzufügen der Auswertung "${name}": ${error.message}`, 'error');
      return false;
    }
  };

  // Update an existing evaluation on the server
  const updateEvaluation = async (originalName, evaluation, content) => {
    try {
      // Make sure we're using the full name with extension when talking to the server
      const fullOriginalName = addEvalExtension(originalName);
      const fullNewName = addEvalExtension(evaluation.evaluationName);
      console.log('Updating evaluation:', { 
        originalName: fullOriginalName,
        newName: fullNewName,
        content
      });
      
      // Use standard encodeURIComponent for URL encoding
      const encodedOriginalName = encodeURIComponent(fullOriginalName);
      console.log('Original name:', fullOriginalName);
      console.log('Encoded name for URL:', encodedOriginalName);
      
      // Make the API call with correctly encoded URL
      const apiUrl = `${getApiBaseUrl()}/api/evaluations/${encodedOriginalName}`;
      console.log('Full API URL:', apiUrl);
      
      // Check if name has changed
      const nameChanged = fullOriginalName !== fullNewName;
      
      if (nameChanged) {
        console.log('Name has changed, will perform rename operation first');
        
        // Find the original evaluation to preserve its position
        const originalEval = evaluations.find(ev => ev.name === fullOriginalName);
        const position = originalEval ? originalEval.position : -1;
        
        try {
          // First create the new evaluation with the new name
          console.log('Creating new evaluation with new name:', fullNewName);
          const createResponse = await fetch(`${getApiBaseUrl()}/api/evaluations`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              name: fullNewName,
              content: content,
              position: position // Preserve the original position
            }),
          });
          
          if (!createResponse.ok) {
            const errorText = await createResponse.text();
            console.error('Error creating new evaluation with new name:', errorText);
            throw new Error(`Failed to create new evaluation: ${errorText}`);
          }
          
          // If creation was successful, delete the old one
          console.log('New evaluation created, now deleting old one:', fullOriginalName);
          const deleteResponse = await fetch(`${getApiBaseUrl()}/api/evaluations/${encodedOriginalName}`, {
            method: 'DELETE',
          });
          
          if (!deleteResponse.ok) {
            const errorText = await deleteResponse.text();
            console.error('Warning: Created new evaluation but failed to delete old one:', errorText);
            // Continue even if deletion fails, as the new evaluation was created successfully
            console.warn('Continuing with old evaluation still present in the system');
          } else {
            console.log('Successfully deleted old evaluation');
          }
          
          // Refresh the evaluations list to show the updated state
          await loadEvaluations();
          showNotification(`Auswertung "${removeEvalExtension(evaluation.evaluationName)}" aktualisiert`);
          return true;
        } catch (error) {
          console.error('Error during rename operation:', error);
          throw new Error(`Failed to rename evaluation: ${error.message}`);
        }
      } else {
        // If name hasn't changed, just update the content
        console.log('Name unchanged, updating content only');
        const response = await fetch(apiUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ content }),
        });
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Server error when updating evaluation content:', errorText);
          if (response.status === 404) {
            throw new Error(`Evaluation not found. This could be due to special characters in the name: ${originalName}`);
          }
          throw new Error(`Failed to update evaluation: ${errorText}`);
        }
        // Always refresh from server to ensure consistency
        await loadEvaluations();
        showNotification(`Auswertung "${removeEvalExtension(evaluation.evaluationName)}" aktualisiert`);
        return true;
      }
    } catch (error) {
      console.error('Error updating evaluation:', error);
      showNotification(`Fehler beim Aktualisieren der Auswertung: ${error.message}`, 'error');
      return false;
    }
  };

  // Delete an evaluation from the server
  const deleteEvaluation = async (nameOrId) => {
    // Make sure we're using the display name (without extension) for user messages
    const displayName = typeof nameOrId === 'string' ? removeEvalExtension(nameOrId) : `ID: ${nameOrId}`;
    if (!window.confirm(`Sind Sie sicher, dass Sie die Auswertung "${displayName}" löschen möchten?`)) {
      return;
    }
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/evaluations/${encodeURIComponent(nameOrId)}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        throw new Error('Failed to delete evaluation');
      }
      
      // Remove from local state - handle both name and id based deletion
      if (typeof nameOrId === 'number') {
        setEvaluations(prev => prev.filter(item => item.id !== nameOrId));
      } else {
        setEvaluations(prev => prev.filter(item => item.name !== nameOrId));
      }
      
      showNotification(`Auswertung "${displayName}" gelöscht`);
    } catch (error) {
      console.error('Error deleting evaluation:', error);
      showNotification(`Fehler beim Löschen der Auswertung "${displayName}"`, 'error');
    }
  };

  // Save services to the server (unified function replacing both saveServices and saveUpdatedServices)
  const saveServices = async (servicesToSave) => {
    try {
      const servicesData = servicesToSave || services;
      console.log('Saving services:', servicesData);
      const response = await fetch(`${getApiBaseUrl()}/api/services`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(servicesData),
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Server responded with error:', errorText);
        throw new Error('Failed to save services');
      }
      // Reload services to ensure UI reflects current server state
      await loadServices();
      showNotification('Gebührenordnungspositionen erfolgreich gespeichert');
      return true;
    } catch (error) {
      console.error('Error saving services:', error);
      showNotification('Fehler beim Speichern der Gebührenordnungspositionen', 'error');
      return false;
    }
  };

  // Handle beginning of service edit
  const handleEditService = (service) => {
    // Store the original code to use it for finding the service later
    setCurrentService({ 
      ...service, 
      originalCode: service.code
    });
    setShowEditServiceModal(true);
  };

  // Add a new service
  const addService = async () => {
    if (!currentService.code.trim() || !currentService.description.trim()) {
      showNotification('Bitte geben Sie Code und Beschreibung an', 'error');
      return;
    }
    // Check if service code already exists
    if (services.some(service => service.code === currentService.code)) {
      showNotification('Eine Gebührenordnungsposition mit diesem Code existiert bereits', 'error');
      return;
    }
    console.log('Adding service:', currentService);
    // Create a new array with the added service
    const updatedServices = [...services, { 
      code: currentService.code, 
      description: currentService.description 
    }];
    console.log('Services before adding:', services);
    console.log('Services after adding:', updatedServices);
    // Update local state
    setServices(updatedServices);
    // Save directly using the updated array
    setTimeout(async () => {
      await saveServices(updatedServices);
      setShowAddServiceModal(false);
      setCurrentService({ code: '', description: '' });
    }, 100);
  };

  // Update an existing service
  const updateService = async () => {
    if (!currentService.code.trim() || !currentService.description.trim()) {
      showNotification('Bitte geben Sie Code und Beschreibung an', 'error');
      return;
    }
    // Get the original service to find it in the list
    const originalCode = currentService.originalCode || currentService.code;
    console.log('Updating service:', {
      originalCode,
      newCode: currentService.code,
      newDescription: currentService.description
    });
    // Create a new array with the updated service instead of modifying state directly
    const updatedServices = services.map(service => 
      service.code === originalCode 
        ? { code: currentService.code, description: currentService.description } 
        : service
    );
    console.log('Services before update:', services);
    console.log('Services after update:', updatedServices);
    // Set the services state with the new array
    setServices(updatedServices);
    // Need to wait for state update to complete before saving
    setTimeout(async () => {
      await saveServices(updatedServices);
      setShowEditServiceModal(false);
    }, 100);
  };

  // Delete a service
  const deleteService = async (code) => {
    try {
      if (!window.confirm(`Sind Sie sicher, dass Sie die Gebührenordnungsposition "${code}" löschen möchten?`)) {
        return;
      }
      console.log(`Attempting to delete service with code: ${code}`);
      // Create a new array without the service to be deleted
      const updatedServices = services.filter(service => service.code !== code);
      console.log('Services before deletion:', services);
      console.log('Services after deletion:', updatedServices);
      // First update the local state
      setServices(updatedServices);
      // Then save the updated services array directly
      setTimeout(async () => {
        try {
          const success = await saveServices(updatedServices);
          if (success) {
            showNotification(`Gebührenordnungsposition "${code}" erfolgreich gelöscht`);
          }
        } catch (saveError) {
          console.error('Error saving services after deletion:', saveError);
          // Restore the original services if saving fails
          setServices(services);
          showNotification(`Fehler beim Löschen der GOP "${code}": ${saveError.message}`, 'error');
        }
      }, 100);
    } catch (error) {
      console.error('Error in deleteService:', error);
      showNotification(`Fehler beim Löschen der GOP "${code}": ${error.message}`, 'error');
    }
  };

  // Handle opening the edit evaluation form
  const handleOpenEditEvaluationForm = async (item) => {
    try {
      console.log('Opening evaluation for edit:', item);
      // Reset evaluation state before loading new data
      setCurrentEvaluation(createEmptyEvaluation());
      // Store the original name to use when updating
      const deserializedEvaluation = deserializeEvaluation(item.content);
      if (deserializedEvaluation) {
        // Make sure the evaluationName exactly matches what's on the server
        deserializedEvaluation.evaluationName = removeEvalExtension(item.name);
        deserializedEvaluation.originalName = item.name; // Store the original name with extension
        
        // Ensure excludeIfFoundAcrossQuarters has a default value if missing
        if (deserializedEvaluation.excludeIfFoundAcrossQuarters === undefined || deserializedEvaluation.excludeIfFoundAcrossQuarters === null) {
          deserializedEvaluation.excludeIfFoundAcrossQuarters = false;
        }
        
        console.log('Deserialized evaluation with excludeIfFoundAcrossQuarters:', deserializedEvaluation.excludeIfFoundAcrossQuarters);
        
        setCurrentEvaluation(deserializedEvaluation);
        setShowAdvancedView(true); // Show advanced view by default when editing
        setShowEditEvalModal(true);
      } else {
        showNotification('Fehler beim Laden der Auswertung: Ungültiges Format', 'error');
      }
    } catch (error) {
      console.error('Error preparing evaluation for edit:', error);
      showNotification(`Fehler beim Laden der Auswertung: ${error.message}`, 'error');
    }
  };

  // Handle adding a new evaluation
  const handleAddEvaluation = async () => {
    try {
      if (!currentEvaluation.evaluationName.trim()) {
        showNotification('Bitte geben Sie einen Namen für die Auswertung an', 'error');
        return;
      }
      const displayName = currentEvaluation.evaluationName.trim();
      // Check if evaluation already exists (compare without extension)
      if (evaluations.some(item => 
          removeEvalExtension(item.name) === displayName || 
          item.name === displayName)) {
        showNotification('Eine Auswertung mit diesem Namen existiert bereits', 'error');
        return;
      }
      // Serialize the evaluation data
      const serializedEvaluation = serializeEvaluation(currentEvaluation);
      console.log('Serialized evaluation data:', serializedEvaluation);
      // Add the new evaluation to the server
      const success = await addEvaluation(displayName, serializedEvaluation);
      if (success) {
        setShowAddEvalModal(false);
        setCurrentEvaluation(createEmptyEvaluation());
      }
    } catch (error) {
      console.error('Error in handleAddEvaluation:', error);
      showNotification(`Fehler beim Hinzufügen der Auswertung: ${error.message}`, 'error');
    }
  };

  // Handle updating an existing evaluation
  const handleUpdateEvaluation = async () => {
    try {
      if (!currentEvaluation.evaluationName.trim()) {
        showNotification('Der Name der Auswertung darf nicht leer sein', 'error');
        return;
      }
      
      console.log('Current evaluation before serialization:', currentEvaluation);
      console.log('excludeIfFoundAcrossQuarters value:', currentEvaluation.excludeIfFoundAcrossQuarters);
      
      // Get the original name for reference in the API call
      const originalName = currentEvaluation.originalName || 
                          addEvalExtension(currentEvaluation.evaluationName);
      // Serialize the evaluation data
      const serializedEvaluation = serializeEvaluation(currentEvaluation);
      console.log('Serialized evaluation for update:', serializedEvaluation);
      console.log('Serialized fields count:', serializedEvaluation.split('|').length);
      
      // Update the evaluation on the server using the original name to find it
      // but also passing the current evaluation object to handle name changes
      const success = await updateEvaluation(
        originalName, 
        currentEvaluation, 
        serializedEvaluation
      );
      if (success) {
        setShowEditEvalModal(false);
      }
    } catch (error) {
      console.error('Error in handleUpdateEvaluation:', error);
      showNotification(`Fehler beim Aktualisieren der Auswertung: ${error.message}`, 'error');
    }
  };

  // Move an evaluation up or down in the order
  const moveEvaluation = async (index, direction) => {
    try {
      // Create a copy of the evaluations array
      const newEvaluations = [...evaluations];
      // Calculate the new index
      const newIndex = direction === 'up' ? Math.max(0, index - 1) : Math.min(evaluations.length - 1, index + 1);
      // If trying to move beyond limits, do nothing
      if (newIndex === index) return;
      // Swap the positions
      const temp = newEvaluations[index];
      newEvaluations[index] = newEvaluations[newIndex];
      newEvaluations[newIndex] = temp;
      // Update the position properties
      newEvaluations.forEach((item, idx) => {
        item.position = idx;
      });
      // Update state first for immediate visual feedback
      setEvaluations(newEvaluations);
      
      // Save the new positions to the server
      try {
        // Use a more direct approach - flatten the response data to avoid complex objects
        const simpleOrderData = newEvaluations.map((item, idx) => ({
          id: item.id || null, // Include ID if available
          name: item.name,
          position: idx
        }));
        
        console.log('Sending simplified order update:', simpleOrderData);
        
        // Try a different endpoint specifically for reordering
        const response = await fetch(`${getApiBaseUrl()}/api/evaluations/reorder`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(simpleOrderData),
        });
        
        console.log('Order update response status:', response.status);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Server error response:', errorText);
          throw new Error(`Failed to update order: ${response.statusText}`);
        }
        
        const responseData = await response.json();
        console.log('Order update successful:', responseData);
        showNotification('Reihenfolge aktualisiert');
      } catch (error) {
        console.error('Error updating evaluation order:', error);
        showNotification('Fehler beim Aktualisieren der Reihenfolge', 'error');
        // Reload evaluations to restore original order if save failed
        await loadEvaluations();
      }
    } catch (error) {
      console.error('Unexpected error in moveEvaluation:', error);
      showNotification(`Fehler: ${error.message}`, 'error');
    }
  };

  // Handle adding a practice
  const handleAddPractice = async () => {
    if (!currentPractice.name.trim() || !currentPractice.bsnr.trim()) {
      showNotification('Bitte geben Sie Name und BSNR ein', 'error');
      return;
    }
    // BSNR validation: should be numeric and 9 digits
    const bsnr = currentPractice.bsnr.trim();
    if (!/^\d{9}$/.test(bsnr)) {
      showNotification('Die BSNR muss 9 Ziffern enthalten', 'error');
      return;
    }
    // Check if BSNR already exists
    if (practices.some(p => p.bsnr === bsnr)) {
      showNotification('Diese BSNR ist bereits registriert', 'error');
      return;
    }
    try {
      const newPractice = {
        name: currentPractice.name.trim(),
        bsnr: bsnr
      };
      const response = await fetch(`${getApiBaseUrl()}/api/license/practice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newPractice),
      });
      if (!response.ok) {
        const error = await response.text();
        throw new Error(error);
      }
      // The license file is now automatically generated on the server side
      // when adding a practice
      // Refresh practices
      await loadPractices();
      setShowAddPracticeModal(false);
      setCurrentPractice({ name: '', bsnr: '' });
      showNotification(`Praxis "${newPractice.name}" wurde hinzugefügt und Lizenzdatei aktualisiert`);
    } catch (error) {
      console.error('Error adding practice:', error);
      showNotification(`Fehler beim Hinzufügen der Praxis: ${error.message}`, 'error');
    }
  };

  // Handle updating a practice
  const handleUpdatePractice = async () => {
    if (!currentPractice.name.trim() || !currentPractice.bsnr.trim()) {
      showNotification('Bitte geben Sie Name und BSNR ein', 'error');
      return;
    }
    // BSNR validation: should be numeric and 9 digits
    const bsnr = currentPractice.bsnr.trim();
    if (!/^\d{9}$/.test(bsnr)) {
      showNotification('Die BSNR muss 9 Ziffern enthalten', 'error');
      return;
    }
    // Check if BSNR already exists and is not the current one
    const originalBsnr = practices[currentPracticeIndex]?.bsnr;
    if (bsnr !== originalBsnr && practices.some(p => p.bsnr === bsnr)) {
      showNotification('Diese BSNR ist bereits registriert', 'error');
      return;
    }
    try {
      const updatedPractice = {
        name: currentPractice.name.trim(),
        bsnr: bsnr,
        originalBsnr: originalBsnr
      };
      const response = await fetch(`${getApiBaseUrl()}/api/license/practice/${encodeURIComponent(originalBsnr)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedPractice),
      });
      if (!response.ok) {
        const error = await response.text();
        throw new Error(error);
      }
      // The license file is now automatically generated on the server side
      // when updating a practice
      // Refresh practices
      await loadPractices();
      setShowEditPracticeModal(false);
      setCurrentPractice({ name: '', bsnr: '' });
      setCurrentPracticeIndex(-1);
      showNotification(`Praxis "${updatedPractice.name}" wurde aktualisiert und Lizenzdatei aktualisiert`);
    } catch (error) {
      console.error('Error updating practice:', error);
      showNotification(`Fehler beim Aktualisieren der Praxis: ${error.message}`, 'error');
    }
  };

  // Handle deleting a practice
  const handleDeletePractice = async (bsnr) => {
    if (!window.confirm('Sind Sie sicher, dass Sie diese Praxis löschen möchten?')) {
      return;
    }
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/license/practice/${encodeURIComponent(bsnr)}`, {
        method: 'DELETE'
      });
      if (!response.ok) {
        const error = await response.text();
        throw new Error(error);
      }
      // The license file is now automatically generated on the server side
      // when deleting a practice
      // Refresh practices
      await loadPractices();
      showNotification('Praxis wurde gelöscht und Lizenzdatei aktualisiert');
    } catch (error) {
      console.error('Error deleting practice:', error);
      showNotification(`Fehler beim Löschen der Praxis: ${error.message}`, 'error');
    }
  };

  // Function to parse and import multiple practices from text
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [bulkImportText, setBulkImportText] = useState('');
  const [importingPractices, setImportingPractices] = useState(false);
  const [importResults, setImportResults] = useState({ success: 0, failed: 0, skipped: 0, details: [] });
  const [showImportResults, setShowImportResults] = useState(false);

  const handleBulkImport = async () => {
    if (!bulkImportText.trim()) {
      showNotification('Bitte geben Sie Praxisdaten ein', 'error');
      return;
    }
  
    // Parse the text input
    const lines = bulkImportText
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);
  
    if (lines.length === 0) {
      showNotification('Keine gültigen Praxisdaten gefunden', 'error');
      return;
    }
  
    // Confirm import
    if (!window.confirm(`${lines.length} Praxen gefunden. Möchten Sie diese importieren? Dies kann einige Zeit dauern.`)) {
      return;
    }
  
    // Start the import process
    setImportingPractices(true);
    setImportProgress(0);
    
    // Prepare results tracking
    const results = { success: 0, failed: 0, skipped: 0, details: [] };
    
    try {
      // First, validate and parse all practices to import
      const practicesToImport = [];
      const existingBSNRs = new Set(practices.map(p => p.bsnr));
      
      // Pre-process all lines to identify valid practices and duplicates
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        
        // Modified pattern to better handle various separators and whitespace
        // Look for any number sequence at the end of the line
        const lastNumberPattern = /^(.*?)[\t\s]+(\d{9})(?:\s*)$/;
        const match = line.match(lastNumberPattern);
        
        console.log(`Processing line ${i+1}: "${line}"`);
        
        if (!match) {
          console.log(`Line ${i+1} failed regex match, trying alternative pattern`);
          // Try an alternative pattern that looks for any 9-digit number anywhere in the line
          const alternativePattern = /^(.*?)(\d{9})(?:\s*)$/;
          const altMatch = line.match(alternativePattern);
          
          if (!altMatch) {
            console.log(`Line ${i+1} failed all regex matches`);
            results.skipped++;
            results.details.push({
              name: line,
              status: 'skipped',
              message: 'Ungültiges Format (Praxisname gefolgt von 9-stelliger BSNR erwartet)'
            });
            continue;
          }
          
          // Use the alternative match
          const name = altMatch[1].trim();
          const bsnr = altMatch[2].trim();
          
          console.log(`Line ${i+1} matched with alternative pattern: Name="${name}", BSNR="${bsnr}"`);
          
          if (existingBSNRs.has(bsnr)) {
            results.skipped++;
            results.details.push({
              name,
              bsnr,
              status: 'skipped',
              message: 'BSNR bereits vorhanden'
            });
          } else {
            practicesToImport.push({ name, bsnr });
            existingBSNRs.add(bsnr); // Prevent duplicates within the import batch
          }
          continue;
        }
        
        const name = match[1].trim();
        const bsnr = match[2].trim();
        
        console.log(`Line ${i+1} parsed: Name="${name}", BSNR="${bsnr}"`);
        
        if (existingBSNRs.has(bsnr)) {
          results.skipped++;
          results.details.push({
            name,
            bsnr,
            status: 'skipped',
            message: 'BSNR bereits vorhanden'
          });
        } else {
          practicesToImport.push({ name, bsnr });
          existingBSNRs.add(bsnr); // Prevent duplicates within the import batch
        }
      }
      
      console.log(`Found ${practicesToImport.length} valid practices to import`);
      
      // Replace the individual import loop with a single bulk API call
      if (practicesToImport.length > 0) {
        try {
          console.log(`Sending bulk import request with ${practicesToImport.length} practices...`);
          
          const response = await fetch(`${getApiBaseUrl()}/api/license/practices/bulk`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ practices: practicesToImport })
          });
          
          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Server error: ${errorText || response.statusText}`);
          }
          
          const bulkResult = await response.json();
          console.log('Bulk import result:', bulkResult);
          
          // Update results based on bulk response
          results.success = bulkResult.added || 0;
          
          if (bulkResult.details && bulkResult.details.valid) {
            for (const practice of bulkResult.details.valid) {
              results.details.push({
                name: practice.name,
                bsnr: practice.bsnr,
                status: 'success',
                message: 'Erfolgreich importiert'
              });
            }
          }
          
          if (bulkResult.details && bulkResult.details.skipped) {
            for (const practice of bulkResult.details.skipped) {
              results.details.push({
                name: practice.name,
                bsnr: practice.bsnr,
                status: 'skipped',
                message: practice.reason || 'Übersprungen durch Server'
              });
            }
          }
          
        } catch (error) {
          console.error('Error during bulk import:', error);
          
          // Add each failed practice to the results
          for (const practice of practicesToImport) {
            results.failed++;
            results.details.push({
              name: practice.name,
              bsnr: practice.bsnr,
              status: 'failed',
              message: `Fehler beim Bulk-Import: ${error.message}`
            });
          }
        }
      }
      
      // Refresh practice list after import
      await loadPractices();
      
    } catch (error) {
      console.error("Error during bulk import:", error);
      showNotification(`Fehler beim Massenimport: ${error.message}`, 'error');
    } finally {
      setImportingPractices(false);
      setImportProgress(100);
      setImportResults(results);
      setShowImportResults(true);
      
      showNotification(
        `Import abgeschlossen: ${results.success} erfolgreich, ${results.failed} fehlgeschlagen, ${results.skipped} übersprungen`,
        results.success > 0 ? 'success' : 'warning'
      );
    }
  };

  // Handle logo file selection
  const handleLogoChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // Check if file is an SVG
      if (file.type !== 'image/svg+xml') {
        showNotification('Bitte wählen Sie eine SVG-Datei für das Logo', 'error');
        return;
      }
      
      // Set the logo file to state
      setLogo(file);
      
      // Generate preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle favicon file selection
  const handleFaviconChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // Check if file is an ICO file
      if (file.type !== 'image/x-icon' && file.type !== 'image/vnd.microsoft.icon' && !file.name.endsWith('.ico')) {
        showNotification('Bitte wählen Sie eine ICO-Datei für das Favicon', 'error');
        return;
      }
      
      // Set the favicon file to state
      setFavicon(file);
      
      // Generate preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setFaviconPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle upload of logo and favicon
  const handleAppearanceUpdate = async () => {
    try {
      setAppearanceUpdating(true);
      
      // Only upload logo if it was changed
      if (logo) {
        const logoFormData = new FormData();
        logoFormData.append('file', logo);
        
        const logoResponse = await fetch(`${getApiBaseUrl()}/api/appearance/logo`, {
          method: 'POST',
          body: logoFormData,
        });
        
        if (!logoResponse.ok) {
          throw new Error('Failed to upload logo');
        }
      }
      
      // Only upload favicon if it was changed
      if (favicon) {
        const faviconFormData = new FormData();
        faviconFormData.append('file', favicon);
        
        const faviconResponse = await fetch(`${getApiBaseUrl()}/api/appearance/favicon`, {
          method: 'POST',
          body: faviconFormData,
        });
        
        if (!faviconResponse.ok) {
          throw new Error('Failed to upload favicon');
        }
      }
      
      showNotification('Erscheinungsbild erfolgreich aktualisiert. Änderungen werden nach einem Neustart wirksam.');
      
      // Reset file states
      setLogo(null);
      setFavicon(null);
      
      // Reload previews with cache-busting parameter
      loadAppearanceSettings();
    } catch (error) {
      console.error('Error updating appearance:', error);
      showNotification(`Fehler beim Aktualisieren des Erscheinungsbildes: ${error.message}`, 'error');
    } finally {
      setAppearanceUpdating(false);
    }
  };

  // Handle application name update
  const handleAppNameUpdate = async () => {
    if (!appNameInput.trim()) {
      showNotification('Bitte geben Sie einen Namen für die Anwendung ein', 'error');
      return;
    }
    
    try {
      setAppearanceUpdating(true);
      
      const response = await fetch(`${getApiBaseUrl()}/api/appearance/app-name`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ appName: appNameInput }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to update application name');
      }
      
      setAppName(appNameInput);
      showNotification('Anwendungsname erfolgreich aktualisiert');
      
      // Update document title if we're on the admin page
      document.title = `${appNameInput} Admin`;
      
    } catch (error) {
      console.error('Error updating application name:', error);
      showNotification(`Fehler beim Aktualisieren des Anwendungsnamens: ${error.message}`, 'error');
    } finally {
      setAppearanceUpdating(false);
    }
  };

  // Add migrateEvaluations function 
  const migrateEvaluations = async () => {
    if (!window.confirm('Möchten Sie wirklich alle Auswertungen aus den Dateien in die Datenbank migrieren? Bestehende Daten in der Datenbank könnten überschrieben werden.')) {
      return;
    }
    
    setIsMigrating(true);
    
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/evaluations/migrate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Migrationsfehler');
      }
      
      setMigrationResult(result);
      setShowMigrationModal(true);
      
      // Reload evaluations to show the migrated data
      await loadEvaluations();
      showNotification(`${result.migrated} Auswertungen erfolgreich migriert.`);
    } catch (error) {
      console.error('Error during migration:', error);
      showNotification(`Fehler bei der Migration: ${error.message}`, 'error');
    } finally {
      setIsMigrating(false);
    }
  };

  // Format date from ISO string to local date and time
  const formatDateTime = (isoString) => {
    if (!isoString) return 'N/A';
    
    try {
      const date = new Date(isoString);
      return date.toLocaleString('de-DE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch (e) {
      console.error('Error formatting date:', e);
      return isoString || 'N/A';
    }
  };

  // Format the status of a login attempt
  const formatLoginStatus = (success) => {
    if (success === true) return 'Erfolgreich';
    if (success === false) return 'Fehlgeschlagen';
    return 'Unbekannt';
  };

  // Function to handle page change in logs pagination
  const handleLogsPageChange = (event, newPage) => {
    setLogsPagination(prev => ({
      ...prev,
      page: newPage
    }));
  };

  // Handler for changing logs filter
  const handleLogsFilterChange = (filterType, value) => {
    // Reset to first page when filter changes
    setLogsPagination(prev => ({
      ...prev,
      page: 0
    }));
    
    if (filterType === 'date') {
      setLogsDateFilter(value);
    } else if (filterType === 'status') {
      setLogsStatusFilter(value);
    } else if (filterType === 'bsnr') {
      setLogsBsnrFilter(value);
    }
  };

  return (
    <Box sx={{ padding: 2 }}>
      <CssBaseline />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography level="h1">{appName} Admin</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {adminUser && (
            <Typography level="body-sm" sx={{ alignSelf: 'center', mr: 2 }}>
              Angemeldet als: {adminUser.username}
            </Typography>
          )}
          <Button 
            variant="outlined" 
            color="primary" 
            onClick={() => setShowChangePasswordModal(true)}
          >
            Passwort ändern
          </Button>
          <Button 
            variant="outlined" 
            color="danger" 
            onClick={handleLogout}
          >
            Abmelden
          </Button>
          <Button 
            variant="outlined" 
            color="neutral" 
            onClick={() => window.location.href = '/'}
          >
            Zurück zur Hauptseite
          </Button>
        </Box>
      </Box>
      {notification.show && (
        <Alert 
          color={notification.type} 
          variant="soft" 
          sx={{ mb: 2 }}
        >
          {notification.message}
        </Alert>
      )}
      <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)}>
        <TabList>
          <Tab>Auswertungen</Tab>
          <Tab>Gebührenordnungspositionen</Tab>
          <Tab>ICD-10 Datenbank</Tab>
          <Tab>Vorhaltepauschale</Tab>
          <Tab>Lizenzverwaltung</Tab>
          <Tab>Erscheinungsbild</Tab>
          <Tab>Login-Protokolle</Tab>
        </TabList>
        <TabPanel value={0}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography level="h2">Auswertungen verwalten</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                color="neutral" 
                variant="outlined"
                onClick={migrateEvaluations}
                disabled={isMigrating}
              >
                Auswertungen aus Dateien importieren
              </Button>
              <Button 
                color="primary" 
                onClick={() => {
                  setCurrentEvaluation(createEmptyEvaluation());
                  setShowAdvancedView(false);
                  setShowAddEvalModal(true);
                }}
              >
                Neue Auswertung hinzufügen
              </Button>
            </Box>
          </Box>
          <Sheet>
            <Table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th style={{ width: '100px' }}>Reihenfolge</th>
                  <th style={{ width: '200px' }}>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {evaluations.map((item, index) => (
                  <tr key={index}> 
                    <td>{removeEvalExtension(item.name)}</td>
                    <td>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton
                          size="sm" 
                          variant="soft"
                          color="neutral"
                          onClick={() => moveEvaluation(index, 'up')}
                          disabled={index === 0}
                        >
                          <ArrowUpwardIcon />
                        </IconButton>
                        <IconButton
                          size="sm"
                          variant="soft"
                          color="neutral"
                          onClick={() => moveEvaluation(index, 'down')}
                          disabled={index === evaluations.length - 1}
                        >
                          <ArrowDownwardIcon />
                        </IconButton>
                      </Box>
                    </td>
                    <td>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button 
                          color="primary" 
                          size="sm" 
                          variant="soft" 
                          onClick={() => handleOpenEditEvaluationForm(item)}
                        >
                          Bearbeiten
                        </Button>
                        <Button 
                          color="danger" 
                          size="sm" 
                          variant="soft" 
                          onClick={() => deleteEvaluation(item.name)}
                        >
                          Löschen
                        </Button>
                      </Box>
                    </td>
                  </tr>
                ))}
                {evaluations.length === 0 && (
                  <tr>
                    <td colSpan={3} style={{ textAlign: 'center' }}>
                      Keine Auswertungen vorhanden
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Sheet>
        </TabPanel>
        <TabPanel value={1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography level="h2">Gebührenordnungspositionen verwalten</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                color="primary" 
                onClick={() => {
                  setCurrentService({ code: '', description: '' });
                  setShowAddServiceModal(true);
                }}
              >
                Neue GOP hinzufügen
              </Button>
            </Box>
          </Box>
          <Sheet>
            <Table>
              <thead>
                <tr>
                  <th style={{ width: '100px' }}>Code</th>
                  <th>Beschreibung</th>
                  <th style={{ width: '200px' }}>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {services.map((service, index) => (
                  <tr key={index}>
                    <td>{service.code}</td>
                    <td>{service.description}</td>
                    <td>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button 
                          color="primary" 
                          size="sm" 
                          variant="soft" 
                          onClick={() => handleEditService(service)}
                        >
                          Bearbeiten
                        </Button>
                        <Button 
                          color="danger" 
                          size="sm" 
                          variant="soft" 
                          onClick={() => deleteService(service.code)}
                        >
                          Löschen
                        </Button>
                      </Box>
                    </td>
                  </tr>
                ))}
                {services.length === 0 && (
                  <tr>
                    <td colSpan={3} style={{ textAlign: 'center' }}>
                      Keine Gebührenordnungspositionen vorhanden
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Sheet>
        </TabPanel>
        <TabPanel value={2}>
          {/* ICD-10 Database Management Tab */}
          <IcdManager />
        </TabPanel>
        <TabPanel value={3}>
          {/* Vorhaltepauschale Management Tab */}
          <VorhaltepauschaleManager />
        </TabPanel>
        <TabPanel value={4}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography level="h2">Lizenzverwaltung</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                color="primary" 
                onClick={() => {
                  setCurrentPractice({ name: '', bsnr: '' });
                  setShowAddPracticeModal(true);
                }}
                startDecorator={<AddCircleOutlineIcon />}
              >
                Neue Praxis hinzufügen
              </Button>
              <Button
                color="neutral"
                variant="outlined"
                onClick={() => {
                  setBulkImportText('');
                  setShowBulkImportModal(true);
                  setShowImportResults(false);
                }}
              >
                Mehrere Praxen importieren
              </Button>
            </Box>
          </Box>
          <Typography level="body1" sx={{ mb: 2 }}>
            Hier können Sie Praxen und deren Betriebsstättennummern (BSNR) verwalten. 
            Die Lizenzdatei wird bei jeder Änderung automatisch aktualisiert und signiert.
          </Typography>
          {loadingLicense ? (
            <Typography level="body1">Lade Lizenzdaten...</Typography>
          ) : practices.length === 0 ? (
            <Card variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
              <Typography level="body1">
                Keine Praxen vorhanden. Bitte fügen Sie eine Praxis hinzu.
              </Typography>
            </Card>
          ) : (
            <Sheet>
              <Table>
                <thead>
                  <tr>
                    <th>Praxisname</th>
                    <th>BSNR</th>
                    <th style={{ width: '200px' }}>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {practices.map((practice, index) => (
                    <tr key={index}>
                      <td>{practice.name}</td>
                      <td>
                        <Chip variant="soft" color="neutral">
                          {practice.bsnr} {/* Display the actual BSNR */}
                        </Chip>
                      </td>
                      <td>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button 
                            color="primary" 
                            size="sm" 
                            variant="soft"
                            onClick={() => {
                              setCurrentPractice({ 
                                name: practice.name, 
                                bsnr: practice.bsnr 
                              });
                              setCurrentPracticeIndex(index);
                              setShowEditPracticeModal(true);
                            }}
                          >
                            Bearbeiten
                          </Button>
                          <IconButton 
                            color="danger" 
                            size="sm" 
                            variant="soft" 
                            onClick={() => handleDeletePractice(practice.bsnr)}
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        </Box>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Sheet>
          )}
        </TabPanel>
        <TabPanel value={5}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography level="h2">Erscheinungsbild anpassen</Typography>
          </Box>
          <Typography level="body1" sx={{ mb: 2 }}>
            Hier können Sie das Logo, Favicon und den Namen der Anwendung anpassen.
            Änderungen am Namen werden sofort wirksam, eventuell muss die Seite aber neugeladen werden.
          </Typography>
          
          <Card variant="outlined" sx={{ mb: 4, p: 2 }}>
            <Typography level="h3" sx={{ mb: 2 }}>Anwendungsname</Typography>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: 'flex-start' }}>
              <FormControl sx={{ flexGrow: 1 }}>
                <FormLabel>Name der Anwendung</FormLabel>
                <Input
                  fullWidth
                  value={appNameInput}
                  onChange={(e) => setAppNameInput(e.target.value)}
                  placeholder="Anwendungsname eingeben"
                />
              </FormControl>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleAppNameUpdate}
                loading={appearanceUpdating}
                disabled={!appNameInput.trim() || appNameInput === appName}
                sx={{ mt: { xs: 0, md: 3.5 } }}
              >
                Namen aktualisieren
              </Button>
            </Box>
            <Typography level="body2" sx={{ mt: 1, color: 'text.secondary' }}>
              Aktueller Name: <strong>{appName}</strong>
            </Typography>
          </Card>
          
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, mb: 4 }}>
            <Card variant="outlined" sx={{ flex: 1, p: 2 }}>
              <Typography level="h3" sx={{ mb: 2 }}>Logo</Typography>
              <Box sx={{ mb: 2 }}>
                <Typography level="body2" sx={{ mb: 1 }}>Aktuelles Logo:</Typography>
                <AspectRatio 
                  ratio="4/3" 
                  objectFit="contain"
                  sx={{ 
                    width: '100%', 
                    bgcolor: 'background.level2',
                    borderRadius: 'sm',
                    mb: 2
                  }}
                >
                  <Box 
                    component="img"
                    src={logoPreview || '/logo.svg'}
                    alt="Logo"
                    sx={{ 
                      maxWidth: '100%',
                      maxHeight: '100%'
                    }}
                    onError={(e) => { e.target.src = '/logo.svg'; }}
                  />
                </AspectRatio>
                <Typography level="body2">
                  Format: SVG (Vektorgrafik empfohlen)
                </Typography>
              </Box>
              <FormControl>
                <FormLabel>Neues Logo hochladen</FormLabel>
                <Input 
                  type="file" 
                  onChange={handleLogoChange}
                  slotProps={{
                    input: {
                      accept: 'image/svg+xml'
                    }
                  }}
                />
              </FormControl>
            </Card>
            
            <Card variant="outlined" sx={{ flex: 1, p: 2 }}>
              <Typography level="h3" sx={{ mb: 2 }}>Favicon</Typography>
              <Box sx={{ mb: 2 }}>
                <Typography level="body2" sx={{ mb: 1 }}>Aktuelles Favicon:</Typography>
                <AspectRatio 
                  ratio="1/1"
                  objectFit="contain"
                  sx={{ 
                    width: '64px', 
                    bgcolor: 'background.level2',
                    borderRadius: 'sm',
                    mb: 2
                  }}
                >
                  <Box 
                    component="img"
                    src={faviconPreview || '/favicon.ico'}
                    alt="Favicon"
                    sx={{ 
                      maxWidth: '100%',
                      maxHeight: '100%'
                    }}
                    onError={(e) => { e.target.src = '/favicon.ico'; }}
                  />
                </AspectRatio>
                <Typography level="body2">
                  Format: ICO (16x16, 32x32 Pixel empfohlen)
                </Typography>
              </Box>
              <FormControl>
                <FormLabel>Neues Favicon hochladen</FormLabel>
                <Input 
                  type="file" 
                  onChange={handleFaviconChange}
                  slotProps={{
                    input: {
                      accept: '.ico,image/x-icon,image/vnd.microsoft.icon'
                    }
                  }}
                />
              </FormControl>
            </Card>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
              color="primary" 
              onClick={handleAppearanceUpdate}
              loading={appearanceUpdating}
              disabled={!logo && !favicon}
            >
              Logo & Favicon speichern
            </Button>
          </Box>
        </TabPanel>
        <TabPanel value={6}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography level="h2">Login-Protokolle</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                color="neutral" 
                variant="outlined"
                onClick={loadLoginLogs}
                startDecorator={<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 2v6h-6"></path><path d="M3 12a9 9 0 0 1 15-6.7L21 8"></path><path d="M3 12a9 9 0 0 0 15 6.7L21 16"></path><path d="M21 22v-6h-6"></path></svg>}
                disabled={isLoadingLogs}
              >
                Aktualisieren
              </Button>
            </Box>
          </Box>
          
          <Sheet variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'flex-end' }} flexWrap="wrap">
              <FormControl sx={{ minWidth: 150 }}>
                <FormLabel>Praxis</FormLabel>
                <select 
                  value={logsBsnrFilter}
                  onChange={(e) => handleLogsFilterChange('bsnr', e.target.value)}
                  style={{ 
                    padding: '8px',
                    borderRadius: '6px',
                    border: '1px solid #ccc',
                    width: '100%',
                    maxWidth: '300px'
                  }}
                >
                  <option value="all">Alle Praxen</option>
                  {availablePractices.map(practice => (
                    <option key={practice.bsnr} value={practice.bsnr}>
                      {practice.name}
                    </option>
                  ))}
                </select>
              </FormControl>
              
              <FormControl sx={{ minWidth: 150 }}>
                <FormLabel>Datum</FormLabel>
                <select 
                  value={logsDateFilter}
                  onChange={(e) => handleLogsFilterChange('date', e.target.value)}
                  style={{ 
                    padding: '8px',
                    borderRadius: '6px',
                    border: '1px solid #ccc'
                  }}
                >
                  <option value="all">Alle</option>
                  <option value="today">Heute</option>
                  <option value="week">Diese Woche</option>
                  <option value="month">Diesen Monat</option>
                </select>
              </FormControl>
              
              <FormControl sx={{ minWidth: 150 }}>
                <FormLabel>Status</FormLabel>
                <select 
                  value={logsStatusFilter}
                  onChange={(e) => handleLogsFilterChange('status', e.target.value)}
                  style={{ 
                    padding: '8px',
                    borderRadius: '6px',
                    border: '1px solid #ccc'
                  }}
                >
                  <option value="all">Alle</option>
                  <option value="success">Erfolgreich</option>
                  <option value="failed">Fehlgeschlagen</option>
                </select>
              </FormControl>
              
              <Button 
                variant="soft" 
                color="primary"
                onClick={loadLoginLogs}
                disabled={isLoadingLogs}
                sx={{ ml: 'auto', mt: { xs: 1, md: 0 } }}
              >
                Filter anwenden
              </Button>
            </Stack>
          </Sheet>
          
          {isLoadingLogs ? (
            <Sheet sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Sheet>
          ) : (
            <Sheet>
              <Table>
                <thead>
                  <tr>
                    <th>Zeitpunkt</th>
                    <th>Praxis</th>
                    <th>BSNR</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {loginLogs.map((log, index) => (
                    <tr key={index}>
                      <td>{formatDateTime(log.timestamp)}</td>
                      <td>
                        {log.practiceName ? (
                          <Typography level="body2" noWrap sx={{ maxWidth: '300px' }} title={log.practiceName}>
                            {log.practiceName}
                          </Typography>
                        ) : (
                          <Typography level="body2" color="neutral">
                            (Unbekannt)
                          </Typography>
                        )}
                      </td>
                      <td>
                        <Chip
                          variant="soft"
                          color="neutral"
                        >
                          {log.bsnr}
                        </Chip>
                      </td>
                      <td>
                        <Chip
                          variant="soft"
                          color={log.success ? "success" : "danger"}
                        >
                          {formatLoginStatus(log.success)}
                        </Chip>
                      </td>
                    </tr>
                  ))}
                  {loginLogs.length === 0 && (
                    <tr>
                      <td colSpan={4} style={{ textAlign: 'center' }}>
                        Keine Login-Protokolle gefunden
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
              
              {/* Pagination */}
              {loginLogs.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
                  <Typography level="body2">
                    Gesamt: {logsPagination.totalCount} Einträge
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="sm"
                      variant="plain"
                      color="neutral"
                      onClick={(e) => handleLogsPageChange(e, Math.max(0, logsPagination.page - 1))}
                      disabled={logsPagination.page === 0}
                    >
                      Zurück
                    </Button>
                    <Typography level="body2" sx={{ alignSelf: 'center' }}>
                      Seite {logsPagination.page + 1}
                    </Typography>
                    <Button
                      size="sm"
                      variant="plain"
                      color="neutral"
                      onClick={(e) => handleLogsPageChange(e, logsPagination.page + 1)}
                      disabled={(logsPagination.page + 1) * logsPagination.pageSize >= logsPagination.totalCount}
                    >
                      Weiter
                    </Button>
                  </Box>
                </Box>
              )}
            </Sheet>
          )}
        </TabPanel>
      </Tabs>
      {/* Add Evaluation Modal */}
      <Modal open={showAddEvalModal} onClose={() => setShowAddEvalModal(false)}>
        <ModalDialog sx={{ maxWidth: '800px', width: '90%', maxHeight: '90vh', overflow: 'auto' }}>
          <ModalClose />
          <Typography level="h4">Neue Auswertung hinzufügen</Typography>
          <Divider sx={{ my: 2 }} />
          <EvaluationForm 
            evaluation={currentEvaluation}
            onEvaluationChange={setCurrentEvaluation}
            services={services}
            showAdvancedView={showAdvancedView}
            onToggleAdvancedView={() => setShowAdvancedView(!showAdvancedView)}
            onSave={handleAddEvaluation}
            submitButtonText="Hinzufügen"
          />
        </ModalDialog>
      </Modal>
      {/* Edit Evaluation Modal */}
      <Modal open={showEditEvalModal} onClose={() => setShowEditEvalModal(false)}>
        <ModalDialog sx={{ maxWidth: '800px', width: '90%', maxHeight: '90vh', overflow: 'auto' }}>
          <ModalClose />
          <Typography level="h4">Auswertung bearbeiten</Typography>
          <Divider sx={{ my: 2 }} />
          <EvaluationForm 
            evaluation={currentEvaluation}
            onEvaluationChange={setCurrentEvaluation}
            services={services}
            showAdvancedView={showAdvancedView}
            onToggleAdvancedView={() => setShowAdvancedView(!showAdvancedView)}
            onSave={handleUpdateEvaluation}
            submitButtonText="Speichern"
          />
        </ModalDialog>
      </Modal>
      {/* Add Service Modal */}
      <Modal open={showAddServiceModal} onClose={() => setShowAddServiceModal(false)}>
        <ModalDialog>
          <ModalClose />
          <Typography level="h4">Neue Gebührenordnungsposition hinzufügen</Typography>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Input
              placeholder="Code"
              value={currentService.code}
              onChange={(e) => setCurrentService(prev => ({ ...prev, code: e.target.value }))}
            />
            <Input
              placeholder="Beschreibung"
              value={currentService.description}
              onChange={(e) => setCurrentService(prev => ({ ...prev, description: e.target.value }))}
            />
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
              <Button variant="soft" color="neutral" onClick={() => setShowAddServiceModal(false)}>
                Abbrechen
              </Button>
              <Button color="primary" onClick={addService}>
                Hinzufügen
              </Button>
            </Box>
          </Box>
        </ModalDialog>
      </Modal>
      {/* Edit Service Modal */}
      <Modal open={showEditServiceModal} onClose={() => setShowEditServiceModal(false)}>
        <ModalDialog>
          <ModalClose />
          <Typography level="h4">Gebührenordnungsposition bearbeiten</Typography>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Input
              placeholder="Code"
              value={currentService.code}
              onChange={(e) => setCurrentService(prev => ({ ...prev, code: e.target.value }))}
            />
            <Input
              placeholder="Beschreibung"
              value={currentService.description}
              onChange={(e) => setCurrentService(prev => ({ ...prev, description: e.target.value }))}
            />
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
              <Button variant="soft" color="neutral" onClick={() => setShowEditServiceModal(false)}>
                Abbrechen
              </Button>
              <Button color="primary" onClick={updateService}>
                Speichern
              </Button>
            </Box>
          </Box>
        </ModalDialog>
      </Modal>
      {/* Add Practice Modal */}
      <Modal open={showAddPracticeModal} onClose={() => setShowAddPracticeModal(false)}>
        <ModalDialog>
          <ModalClose />
          <Typography level="h4">Neue Praxis hinzufügen</Typography>
          <Divider sx={{ my: 2 }} />
          <Stack spacing={2}>
            <FormControl>
              <FormLabel>Praxisname</FormLabel>
              <Input
                value={currentPractice.name}
                onChange={(e) => setCurrentPractice(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Name der Praxis eingeben"
              />
            </FormControl>
            <FormControl>
              <FormLabel>Betriebsstättennummer (BSNR)</FormLabel>
              <Input
                value={currentPractice.bsnr}
                onChange={(e) => setCurrentPractice(prev => ({ ...prev, bsnr: e.target.value }))}
                placeholder="9-stellige BSNR eingeben"
                slotProps={{
                  input: {
                    pattern: '[0-9]*',
                    maxLength: 9
                  }
                }}
              />
            </FormControl>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
              <Button variant="soft" color="neutral" onClick={() => setShowAddPracticeModal(false)}>
                Abbrechen
              </Button>
              <Button color="primary" onClick={handleAddPractice}>
                Hinzufügen
              </Button>
            </Box>
          </Stack>
        </ModalDialog>
      </Modal>
      {/* Edit Practice Modal */}
      <Modal open={showEditPracticeModal} onClose={() => setShowEditPracticeModal(false)}>
        <ModalDialog>
          <ModalClose />
          <Typography level="h4">Praxis bearbeiten</Typography>
          <Divider sx={{ my: 2 }} />
                   <Stack spacing={2}>
            <FormControl>
              <FormLabel>Praxisname</FormLabel>
              <Input
                value={currentPractice.name}
                onChange={(e) => setCurrentPractice(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Name der Praxis eingeben"
              />
            </FormControl>
            <FormControl>
              <FormLabel>Betriebsstättennummer (BSNR)</FormLabel>
              <Input
                value={currentPractice.bsnr}
                onChange={(e) => setCurrentPractice(prev => ({ ...prev, bsnr: e.target.value }))}
                placeholder="9-stellige BSNR eingeben"
                slotProps={{
                  input: {
                    pattern: '[0-9]*',
                    maxLength: 9
                  }
                }}
              />
            </FormControl>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
              <Button variant="soft" color="neutral" onClick={() => setShowEditPracticeModal(false)}>
                Abbrechen
              </Button>
              <Button color="primary" onClick={handleUpdatePractice}>
                Speichern
              </Button>
            </Box>
          </Stack>
        </ModalDialog>
      </Modal>
      {/* Bulk Import Practices Modal */}
      <Modal 
        open={showBulkImportModal} 
        onClose={() => !importingPractices && setShowBulkImportModal(false)}
      >
        <ModalDialog 
          sx={{ maxWidth: '800px', width: '90%', maxHeight: '90vh', overflow: 'auto' }}
          layout="center"
        >
          {!importingPractices && <ModalClose />}
          <Typography level="h4">Mehrere Praxen importieren</Typography>
          <Divider sx={{ my: 2 }} />
          
          {!showImportResults ? (
            <Stack spacing={2}>
              <Typography level="body1">
                Fügen Sie Praxisdaten im Format "Praxisname [TAB] BSNR" ein (eine Praxis pro Zeile).
                Die BSNR muss 9 Ziffern enthalten und am Ende der Zeile stehen.
              </Typography>
              
              <textarea
                value={bulkImportText}
                onChange={(e) => setBulkImportText(e.target.value)}
                placeholder="Praxis Dr. Muster	123456789
Gemeinschaftspraxis Dr. A, Dr. B	987654321"
                style={{ 
                  width: '100%', 
                  height: '300px', 
                  padding: '8px',
                  fontFamily: 'monospace',
                  border: '1px solid #ccc',
                  borderRadius: '4px'
                }}
                disabled={importingPractices}
              />
              
              {importingPractices && (
                <Box sx={{ width: '100%' }}>
                  <Typography level="body2" sx={{ mb: 1 }}>
                    Import läuft... Bitte warten Sie, bis der Vorgang abgeschlossen ist.
                  </Typography>
                  <LinearProgress 
                    determinate 
                    value={importProgress} 
                    sx={{ width: '100%' }}
                  />
                  <Typography level="body2" sx={{ mt: 0.5, textAlign: 'center' }}>
                    {importProgress}% abgeschlossen
                  </Typography>
                </Box>
              )}
              
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button 
                  variant="soft" 
                  color="neutral" 
                  onClick={() => setShowBulkImportModal(false)} 
                  disabled={importingPractices}
                >
                  Abbrechen
                </Button>
                <Button 
                  color="primary" 
                  onClick={handleBulkImport}
                  loading={importingPractices} 
                  disabled={!bulkImportText.trim() || importingPractices}
                >
                  Importieren
                </Button>
              </Box>
            </Stack>
          ) : (
            <Stack spacing={2}>
              <Typography level="h5">
                Import Ergebnis
              </Typography>
              
              <Typography level="body1">
                {importResults.success} Praxen erfolgreich importiert
                {importResults.skipped > 0 && `, ${importResults.skipped} übersprungen`}
                {importResults.failed > 0 && `, ${importResults.failed} fehlgeschlagen`}
              </Typography>
              
              <Sheet variant="outlined" sx={{ height: '300px', overflow: 'auto', p: 2 }}>
                {importResults.details.map((result, index) => (
                  <Box 
                    key={index} 
                    sx={{ 
                      mb: 1, 
                      p: 1, 
                      borderLeft: '4px solid',
                      borderColor: result.status === 'success' 
                        ? 'success.500' 
                        : result.status === 'skipped' 
                          ? 'warning.500' 
                          : 'danger.500' 
                    }}
                  >
                    <Typography level="body1" fontWeight="bold">
                      {result.name || result.line}
                      {result.bsnr && ` (${result.bsnr})`}
                    </Typography>
                    <Typography level="body2">
                      Status: {
                        result.status === 'success' 
                          ? '✅ Erfolgreich' 
                          : result.status === 'skipped' 
                            ? '⚠️ Übersprungen' 
                            : '❌ Fehler'
                      }
                    </Typography>
                    {result.message && (
                      <Typography level="body2">
                        {result.message}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Sheet>
              
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button 
                  variant="soft" 
                  color="neutral"
                  onClick={() => {
                    setBulkImportText('');
                    setShowImportResults(false);
                  }}
                >
                  Zurück
                </Button>
                <Button 
                  color="primary" 
                  onClick={() => {
                    setShowBulkImportModal(false);
                  }}
                >
                  Schließen
                </Button>
              </Box>
            </Stack>
          )}
        </ModalDialog>
      </Modal>
      {/* Change password modal */}
      <Modal
        open={showChangePasswordModal}
        onClose={() => !passwordChangeLoading && setShowChangePasswordModal(false)}
      >
        <ModalDialog>
          <ModalClose disabled={passwordChangeLoading} />
          <Typography level="h4">Passwort ändern</Typography>
          <Divider sx={{ my: 2 }} />
          <Stack spacing={2}>
            <FormControl>
              <FormLabel>Aktuelles Passwort</FormLabel>
              <Input
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData(prev => ({
                  ...prev,
                  currentPassword: e.target.value
                }))}
                disabled={passwordChangeLoading}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Neues Passwort</FormLabel>
              <Input
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData(prev => ({
                  ...prev,
                  newPassword: e.target.value
                }))}
                disabled={passwordChangeLoading}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Neues Passwort bestätigen</FormLabel>
              <Input
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData(prev => ({
                  ...prev,
                  confirmPassword: e.target.value
                }))}
                disabled={passwordChangeLoading}
              />
            </FormControl>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="soft"
                color="neutral"
                onClick={() => setShowChangePasswordModal(false)}
                disabled={passwordChangeLoading}
              >
                Abbrechen
              </Button>
              <Button
                color="primary"
                onClick={handleChangePassword}
                loading={passwordChangeLoading}
              >
                Passwort ändern
              </Button>
            </Box>
          </Stack>
        </ModalDialog>
      </Modal>
      {/* Migration result modal */}
      <Modal open={showMigrationModal} onClose={() => setShowMigrationModal(false)}>
        <ModalDialog sx={{ maxWidth: '700px', width: '90%', maxHeight: '90vh', overflow: 'auto' }}>
          <ModalClose />
          <Typography level="h4">Migrationsergebnis</Typography>
          <Divider sx={{ my: 2 }} />
          {migrationResult && (
            <Box>
              <Box sx={{ mb: 2 }}>
                <Typography level="body1">
                  Insgesamt: {migrationResult.total} Auswertungen
                </Typography>
                <Typography level="body1" color="success.main">
                  Erfolgreich migriert: {migrationResult.migrated} Auswertungen
                </Typography>
                {migrationResult.failed > 0 && (
                  <Typography level="body1" color="error.main">
                    Fehler: {migrationResult.failed} Auswertungen
                  </Typography>
                )}
              </Box>
              <Sheet sx={{ height: '400px', overflow: 'auto', p: 1 }}>
                {migrationResult.details.map((detail, index) => (
                  <Box 
                    key={index} 
                    sx={{ 
                      p: 1, 
                      mb: 1, 
                      borderLeft: '3px solid', 
                      borderLeftColor: detail.success ? 'success.main' : 'error.main',
                      bgcolor: detail.success ? 'success.softBg' : 'error.softBg',
                    }}
                  >
                    <Typography level="body1">{detail.name}</Typography>
                    {!detail.success && (
                      <Typography level="body2" color="error.main">
                        Error: {detail.error}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Sheet>
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button onClick={() => setShowMigrationModal(false)}>Schließen</Button>
          </Box>
        </ModalDialog>
      </Modal>
    </Box>
  );
}

export default Admin;